import React, {FC, useState, useRef, useEffect} from 'react'
import {ReactionType} from '@dv5/design/icons/reactions/ReactionType'
import BrokenHeartIcon from '@dv5/design/icons/reactions/broken-heart.svg?react'
import CelebrationIcon from '@dv5/design/icons/reactions/celebration.svg?react'
import CheckboxIcon from '@dv5/design/icons/reactions/checkbox.svg?react'
import CommentIcon from '@dv5/design/icons/reactions/comment.svg?react'
import DislikeIcon from '@dv5/design/icons/reactions/dislike.svg?react'
import FiveIcon from '@dv5/design/icons/reactions/five.svg?react'
import FourIcon from '@dv5/design/icons/reactions/four.svg?react'
import HeartIcon from '@dv5/design/icons/reactions/heart.svg?react'
import HmmmIcon from '@dv5/design/icons/reactions/hmmm.svg?react'
import IdeaIcon from '@dv5/design/icons/reactions/idea.svg?react'
import ImportantIcon from '@dv5/design/icons/reactions/important.svg?react'
import LikeIcon from '@dv5/design/icons/reactions/like.svg?react'
import MinusIcon from '@dv5/design/icons/reactions/minus.svg?react'
import OneIcon from '@dv5/design/icons/reactions/one.svg?react'
import PlusOneIcon from '@dv5/design/icons/reactions/plus-one.svg?react'
import PlusIcon from '@dv5/design/icons/reactions/plus.svg?react'
import QuestionIcon from '@dv5/design/icons/reactions/question.svg?react'
import SadIcon from '@dv5/design/icons/reactions/sad.svg?react'
import ScheduledIcon from '@dv5/design/icons/reactions/scheduled.svg?react'
import SmileIcon from '@dv5/design/icons/reactions/smile.svg?react'
import ThreeIcon from '@dv5/design/icons/reactions/three.svg?react'
import TwoIcon from '@dv5/design/icons/reactions/two.svg?react'
import UncheckedIcon from '@dv5/design/icons/reactions/unchecked.svg?react'
import WaitIcon from '@dv5/design/icons/reactions/wait.svg?react'
import EmptyIcon from '@dv5/design/icons/reactions/empty.svg?react'
import './ReactionPicker.less'

interface ReactionPickerProps {
    value: string
    allowEmpty: boolean
    onChange: (reactionType: string) => void
}

const reactionIconMap: Record<ReactionType, React.FC<React.SVGProps<SVGSVGElement>>> = {
    [ReactionType.None]: EmptyIcon,
    [ReactionType.BrokenHeart]: BrokenHeartIcon,
    [ReactionType.Celebration]: CelebrationIcon,
    [ReactionType.Checkbox]: CheckboxIcon,
    [ReactionType.Comment]: CommentIcon,
    [ReactionType.Dislike]: DislikeIcon,
    [ReactionType.Heart]: HeartIcon,
    [ReactionType.Hmmm]: HmmmIcon,
    [ReactionType.Idea]: IdeaIcon,
    [ReactionType.Important]: ImportantIcon,
    [ReactionType.Like]: LikeIcon,
    [ReactionType.Minus]: MinusIcon,
    [ReactionType.One]: OneIcon,
    [ReactionType.Two]: TwoIcon,
    [ReactionType.Three]: ThreeIcon,
    [ReactionType.Four]: FourIcon,
    [ReactionType.Five]: FiveIcon,
    [ReactionType.PlusOne]: PlusOneIcon,
    [ReactionType.Plus]: PlusIcon,
    [ReactionType.Question]: QuestionIcon,
    [ReactionType.Sad]: SadIcon,
    [ReactionType.Scheduled]: ScheduledIcon,
    [ReactionType.Smile]: SmileIcon,

    [ReactionType.Unchecked]: UncheckedIcon,
    [ReactionType.Wait]: WaitIcon,
}

const REACTION_OPTIONS = Object.values(ReactionType)
const NON_EMPTY_REACTION_OPTIONS = REACTION_OPTIONS.filter(reaction => reaction !== ReactionType.None)

export const ReactionPicker: FC<ReactionPickerProps> = ({
                                                            value,
                                                            allowEmpty,
                                                            onChange,
                                                        }) => {
    const [isOpen, setIsOpen] = useState(false)
    const pickerRef = useRef<HTMLDivElement>(null)

    const handleReactionClick = (event: React.MouseEvent<HTMLButtonElement>, reaction: ReactionType) => {
        event.preventDefault()
        event.stopPropagation()
        onChange(reaction)
        setIsOpen(false)
    }

    const togglePopup = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault()
        event.stopPropagation()
        setIsOpen(prev => !prev)
    }

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
                setIsOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const getIconComponent = (reaction: ReactionType) => {
        const IconComponent = reactionIconMap[reaction]
        if (!IconComponent) return null
        return <IconComponent width="20" height="20"/>
    }
    const reactionTypes = allowEmpty ? REACTION_OPTIONS : NON_EMPTY_REACTION_OPTIONS

    return (
        <div className="reaction-picker" ref={pickerRef}>
            <button
                type="button"
                className="reaction-picker__trigger"
                onMouseDown={togglePopup}
                aria-expanded={isOpen}
                aria-label="Toggle reaction picker"
            >
                {value === ReactionType.None ? (
                    <EmptyIcon width="20" height="20"/>
                ) : (
                    getIconComponent(value as ReactionType)
                )}
            </button>
            {isOpen && (
                <div className="reaction-picker__popup">
                    {reactionTypes.map((reaction) => (
                        <button
                            key={reaction}
                            className={`reaction-picker__option ${value === reaction ? 'reaction-picker__option--selected' : ''}`}
                            onMouseDown={event => handleReactionClick(event, reaction)}
                            aria-label={`Select reaction ${reaction}`}
                            type="button"
                        >
                            {getIconComponent(reaction)}
                        </button>
                    ))}
                </div>
            )}
        </div>
    )
}

export const getReactionIcon = (reactionType: string): React.FC<React.SVGProps<SVGSVGElement>> | null => {
    return reactionIconMap[reactionType as ReactionType] || null
}
